import { Reducer } from 'redux';

import { reducer } from '../routes/index';
import { combineReducersCustom } from '../service/Redux/Redux';
import { GridInitialData } from '../typings/grid';
import { State } from '../typings/state';

import { addChatListEditor } from './addChatListEditor/addChatListEditor';
import { bannerCreativePreviewPopupReducer } from './bannerCreativePreviewPopup';
import { bannersEditMassActions } from './banners-edit/banners-edit-mass-actions/banners-edit-mass-actions';
import { calloutsReducer } from './banners-edit/callouts';
import { pricesReducer } from './banners-edit/prices';
import { bannerSiteLinksEditorReducer } from './banners-edit/sitelinks';
import { bannersMultiEdit as editBanners } from './bannersEdit';
import { campaignsEdit as editCampaigns } from './campaignsEdit';
import { chatListEditorReducer } from './chatListEditor/chatListEditor';
import { columnsSettingsEditor } from './columnsSettingsEditor';
import { CommonInitializationData, getCommonData } from './common';
import { commonPriceEditor } from './commonPriceEditor';
import { creative } from './creative';
import { datesIntervalEditor } from './datesIntervalEditor';
import { deal as deals } from './deals';
import { devInfo } from './devInfo';
import { editPageItemNameEditor } from './editPageItemName/editPageItemName';
import { experimentsEditor } from './experimentsEditor';
import { feedFiltersRootReducer as feedFilters } from './feed-filters';
import { freelancers } from './freelancers';
import { goalsEdit } from './goals';
import { getGrid } from './grid';
import { groupsEditMassActions } from './groups-edit/groups-edit-mass-actions/groups-edit-mass-actions';
import { groupsMultiEdit as editGroups } from './groupsEdit';
import { getHeader, HeaderInitialData } from './header';
import { help } from './help';
import { imageEditorReducer } from './imageEditor';
import { imageSmartCenterEditor } from './imageSmartCenterEditor';
import { indoorMapEditor } from './indoorMapEditor';
import { internalBannersMultiEdit } from './internalBannersEdit';
import { itemsListTextareaEditor } from './itemsListTextareaEditor/itemsListTextareaEditor';
import { log } from './log';
import { makePricesWizardReducer } from './make-prices-wizard-reducer';
import { marketerCard } from './marketers/marketerCard';
import { marketersList } from './marketers/marketersList';
import { metrikaCounterEditor } from './metrikaCounterEditor';
import { metrikaKeyGoalsEditor } from './metrikaKeyGoals';
import { minusPhraseLibrary } from './minus-phrase-library';
import { minusPhraseLibraryEditor } from './minus-phrase-library-editor';
import { mobileAppsCreate } from './mobile-app';
import { mobileAppsList } from './mobile-apps-list';
import { notificationEditor } from './notificationEditor';
import { organizationsData } from './organizationsData';
import { organizationsEditor } from './organizationsEditor';
import { outdoorMapEditor } from './outdoorMapEditor';
import { phrasesSelectorWizard } from './phrasesSelectorWizard';
import { platformsPreviewEditor } from './platformsPreviewEditor';
import { predictor } from './predictor';
import { predictorEditor } from './predictorEditor';
import { products } from './products';
import { rateCorrectionsEditor } from './rateCorrectionsEditor';
import { recommendationModalReducer } from './recommendationModal';
import { recommendations } from './recommendations';
import { recommendationsGrid } from './recommendationsGrid';
import { regions } from './regions';
import { regionsEditor } from './regionsEditor';
import { relevantPhrasesEditor } from './relevantPhrases';
import { reportClient } from './reportsClient';
import { reportDomain } from './reportsDomain';
import { retargetingConditionEditor } from './retargetingConditionEditor';
import { retargetingEditor } from './retargetingEditor';
import { withRouteFlow } from './routeFlow';
import { strategyEdiorReducer } from './strategyEditor';
import { tagCloudTargeting } from './tagCloudTargeting';
import { cryptaAudioGenresEditor } from './targetings/cryptaAudioGenres';
import { cryptaBehavioralEditor } from './targetings/cryptaBehavioral';
import { cryptaGroupFamilyEditor } from './targetings/cryptaGroupFamily';
import { cryptaSocialDemoEditor } from './targetings/cryptaSocialDemo';
import { interestsTargetingEditor } from './targetings/interestsEditor';
import { timeTargetingEditor } from './timeTargetingEditor';
import { turbolandings } from './turbolandings';
import { video } from './video';
import { visitingCardEditor } from './visitingCardEditor';
import { getWelcome, WelcomeInitializationData } from './welcome';

export interface InitializationData {
    commonData: CommonInitializationData;
    grid: GridInitialData;
    header: HeaderInitialData;
    welcome?: WelcomeInitializationData;
}

export function getRootReducer(initData: InitializationData): Reducer<State> {
    const combinedReducer: Reducer<State> = combineReducersCustom(
        withRouteFlow({
            commonData: getCommonData(initData.commonData),
            welcome: getWelcome(initData.welcome),
            imageSmartCenterEditor,
            grid: getGrid(initData.grid),
            header: getHeader(initData.header),
            creative,
            log,
            reportClient,
            reportDomain,
            phrasesSelectorWizard,
            router: reducer,
            mobileAppsList,
            mobileAppsCreate,
            recommendationsGrid,
            recommendations,
            calloutsEditor: calloutsReducer,
            help,
            siteLinksEditor: bannerSiteLinksEditorReducer,
            bannersPriceEditor: pricesReducer,
            video,
            editBanners,
            bannersEditMassActions,
            ...freelancers,
            deals,
            editCampaigns,
            turbolandings,
            editInternalBanners: internalBannersMultiEdit,
            editGroups,
            regions,
            devInfo,
            rateCorrectionsEditor,
            interestsTargetingEditor,
            cryptaSocialDemoEditor,
            cryptaGroupFamilyEditor,
            cryptaBehavioralEditor,
            cryptaAudioGenresEditor,
            retargetingEditor,
            retargetingConditionEditor,
            outdoorMapEditor,
            indoorMapEditor,
            platformsPreviewEditor,
            regionsEditor,
            minusPhraseLibrary,
            predictor,
            columnsSettingsEditor,
            goalsData: goalsEdit,
            feedFilters,
            groupsEditMassActions,
            minusPhraseLibraryEditor,
            commonPriceEditor,
            chatListEditor: chatListEditorReducer,
            addChatListEditor,
            metrikaCounterEditor,
            relevantPhrasesEditor,
            imageEditor: imageEditorReducer,
            datesIntervalEditor,
            editPageItemNameEditor,
            bannerCreativePreviewPopup: bannerCreativePreviewPopupReducer,
            recommendationsModal: recommendationModalReducer,
            itemsListTextareaEditor,
            metrikaKeyGoalsEditor,
            notificationEditor,
            visitingCardEditor,
            experimentsEditor,
            products,
            marketerCard,
            marketersList,
            organizationsEditor,
            organizationsData,
            strategyEditor: strategyEdiorReducer,
            timeTargetingEditor,
            tagCloudTargetingEditor: tagCloudTargeting,
            predictorEditor
        })
    );

    const pricesWizardReducer = makePricesWizardReducer(initData.grid);

    return (state, action): State => {
        const newState = combinedReducer(state, action);

        return pricesWizardReducer(newState, action, state);
    };
}
